import React from "react";
import { X } from "lucide-react";

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function money(value) {
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(num(value));
}

function dt(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function titleCase(value) {
  return String(value || "-")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function firstValue(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function isUuidText(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || "").trim());
}

function firstNonUuid(...values) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text && !isUuidText(text)) return text;
  }
  return null;
}

function getPayload(detail = {}) {
  return detail?.data || detail || {};
}

function InfoBox({ label, value }) {
  return (
    <div className="rounded-[14px] border border-[#f3c7ce] bg-white px-4 py-3">
      <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#df6374]">{label}</p>
      <p className="mt-1 break-words text-[13px] font-bold text-slate-700">{value ?? "-"}</p>
    </div>
  );
}

function MoneyBox({ label, value, helper }) {
  return (
    <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4 shadow-sm">
      <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#df6374]">{label}</p>
      <p className="mt-2 text-[22px] font-black text-black">₹ {money(value)}</p>
      {helper ? <p className="mt-1 text-[11px] font-bold text-slate-400">{helper}</p> : null}
    </div>
  );
}

function CountBox({ label, value, helper }) {
  return (
    <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4 shadow-sm">
      <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#df6374]">{label}</p>
      <p className="mt-2 text-[22px] font-black text-black">{num(value)}</p>
      {helper ? <p className="mt-1 text-[11px] font-bold text-slate-400">{helper}</p> : null}
    </div>
  );
}

function Pill({ children, tone = "slate" }) {
  const cls = tone === "green"
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : tone === "red"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : tone === "amber"
        ? "border-amber-200 bg-amber-50 text-amber-700"
        : "border-slate-200 bg-slate-50 text-slate-700";
  return <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-black ${cls}`}>{children}</span>;
}

function statusTone(status) {
  const s = String(status || "").toUpperCase();
  if (["PAID", "APPROVED", "ACTIVE", "DELIVERED", "COMPLETED", "SUCCESS", "CREDIT"].includes(s)) return "green";
  if (["FAILED", "REJECTED", "CANCELLED", "DEBIT"].includes(s)) return "red";
  if (["PENDING", "REQUESTED", "PROCESSING", "ASSIGNED", "OUT_FOR_DELIVERY"].includes(s)) return "amber";
  return "slate";
}

function EmptyMini({ text = "No data found" }) {
  return <div className="rounded-[14px] border border-dashed border-[#f3c7ce] bg-white p-5 text-center text-sm font-bold text-slate-400">{text}</div>;
}

function OrdersTable({ rows = [] }) {
  if (!rows.length) return <EmptyMini text="No order/income history found for this driver" />;

  return (
    <div className="max-h-[360px] overflow-auto rounded-[14px] border border-[#f3c7ce] bg-white">
      <table className="min-w-full border-collapse text-left">
        <thead className="sticky top-0 z-10 bg-[#fff0f3] text-[11px] font-black uppercase tracking-[0.08em] text-[#df6374]">
          <tr>
            <th className="px-4 py-3">Order</th>
            <th className="px-4 py-3">Task status</th>
            <th className="px-4 py-3">Customer</th>
            <th className="px-4 py-3">Warehouse</th>
            <th className="px-4 py-3 text-right">Order total</th>
            <th className="px-4 py-3 text-right">Driver income</th>
            <th className="px-4 py-3">Delivered</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 text-[12px] font-semibold text-slate-600">
          {rows.map((row) => (
            <tr key={row.taskId || row.task_id || row.orderId || row.order_id} className="hover:bg-[#fff8f9]">
              <td className="px-4 py-3 font-black text-slate-800">{firstValue(row.orderNumber, row.order_number, row.orderId, row.order_id)}</td>
              <td className="px-4 py-3"><Pill tone={statusTone(row.taskStatus || row.task_status)}>{titleCase(row.taskStatus || row.task_status)}</Pill></td>
              <td className="px-4 py-3">{firstValue(row.customerName, row.customer_name, row.customerPhone, row.customer_phone, "-")}</td>
              <td className="px-4 py-3">{firstValue(row.warehouseName, row.warehouse_name, row.warehouseId, row.warehouse_id, "-")}</td>
              <td className="px-4 py-3 text-right">₹ {money(row.grandTotal ?? row.grand_total)}</td>
              <td className="px-4 py-3 text-right font-black text-emerald-700">₹ {money(row.earnedAmount ?? row.earned_amount)}</td>
              <td className="px-4 py-3">{dt(row.deliveredAt || row.delivered_at || row.updatedAt || row.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TransactionsTable({ rows = [] }) {
  if (!rows.length) return <EmptyMini text="No wallet transactions found" />;

  return (
    <div className="max-h-[260px] overflow-auto rounded-[14px] border border-[#f3c7ce] bg-white">
      <table className="min-w-full border-collapse text-left">
        <thead className="sticky top-0 z-10 bg-[#fff0f3] text-[11px] font-black uppercase tracking-[0.08em] text-[#df6374]">
          <tr>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Direction</th>
            <th className="px-4 py-3 text-right">Amount</th>
            <th className="px-4 py-3 text-right">Balance after</th>
            <th className="px-4 py-3">Note</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 text-[12px] font-semibold text-slate-600">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-[#fff8f9]">
              <td className="px-4 py-3">{dt(row.createdAt || row.created_at)}</td>
              <td className="px-4 py-3 font-black text-slate-800">{titleCase(row.type)}</td>
              <td className="px-4 py-3"><Pill tone={statusTone(row.direction)}>{titleCase(row.direction)}</Pill></td>
              <td className="px-4 py-3 text-right">₹ {money(row.amount)}</td>
              <td className="px-4 py-3 text-right">{row.balanceAfter == null && row.balance_after == null ? "-" : `₹ ${money(row.balanceAfter ?? row.balance_after)}`}</td>
              <td className="px-4 py-3">{row.note || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WithdrawalsTable({ rows = [] }) {
  if (!rows.length) return <EmptyMini text="No withdrawal history found" />;

  return (
    <div className="max-h-[260px] overflow-auto rounded-[14px] border border-[#f3c7ce] bg-white">
      <table className="min-w-full border-collapse text-left">
        <thead className="sticky top-0 z-10 bg-[#fff0f3] text-[11px] font-black uppercase tracking-[0.08em] text-[#df6374]">
          <tr>
            <th className="px-4 py-3">Requested</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Amount</th>
            <th className="px-4 py-3 text-right">Payout</th>
            <th className="px-4 py-3">Reference</th>
            <th className="px-4 py-3">Paid</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 text-[12px] font-semibold text-slate-600">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-[#fff8f9]">
              <td className="px-4 py-3">{dt(row.requestedAt || row.requested_at)}</td>
              <td className="px-4 py-3"><Pill tone={statusTone(row.status)}>{titleCase(row.status)}</Pill></td>
              <td className="px-4 py-3 text-right">₹ {money(row.amount)}</td>
              <td className="px-4 py-3 text-right">₹ {money(row.payoutAmount ?? row.payout_amount ?? row.manualPayableAmount ?? row.manual_payable_amount)}</td>
              <td className="px-4 py-3">{firstValue(row.payoutReference, row.payout_reference, "-")}</td>
              <td className="px-4 py-3">{dt(row.paidAt || row.paid_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DriverDetailModal({ open, loading, error, detail, onClose, ordersPage = 1, ordersLimit = 10, onOrdersPageChange = null, fallbackDriver = null }) {
  if (!open) return null;

  const payload = getPayload(detail);
  const driver = payload.driver || {};
  const presence = payload.presence || {};
  const wallet = payload.wallet || {};
  const summary = payload.summary || {};
  const orders = payload.orders || [];
  const ordersPagination = payload.ordersPagination || payload.orders_pagination || {};
  const ordersTotal = Number(ordersPagination.total ?? summary.totalOrders ?? summary.total_orders ?? orders.length ?? 0);
  const totalOrderPages = Math.max(1, Math.ceil(ordersTotal / Math.max(1, Number(ordersLimit || 10))));
  const transactions = payload.walletTransactions || payload.wallet_transactions || [];
  const withdrawals = payload.withdrawals || [];
  const bankAccounts = payload.bankAccounts || payload.bank_accounts || [];
  const primaryBank = bankAccounts.find((x) => x.isPrimary || x.is_primary) || bankAccounts[0] || null;

  const fallback = fallbackDriver || {};
  const driverName = firstNonUuid(
    driver.name,
    driver.driverName,
    driver.driver_name,
    driver.fullName,
    driver.full_name,
    fallback.driver_name,
    fallback.driverName,
    fallback.driver,
    fallback.name,
    driver.phone,
    fallback.driver_phone,
    fallback.phone,
    driver.partnerCode,
    driver.partner_code,
    fallback.partner_code,
    fallback.partnerCode
  ) || "Unknown Driver";
  const driverCode = firstNonUuid(driver.partnerCode, driver.partner_code, fallback.partner_code, fallback.partnerCode);
  const driverPhone = firstValue(driver.phone, fallback.driver_phone, fallback.phone, "-");
  const driverEmail = firstValue(driver.email, fallback.driver_email, fallback.email, "-");
  const vehicle = [
    firstValue(driver.vehicleType, driver.vehicle_type, fallback.vehicle_type, fallback.vehicleType),
    firstValue(driver.vehicleNumber, driver.vehicle_number, fallback.vehicle_number, fallback.vehicleNumber),
  ].filter(Boolean).join(" / ") || "-";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[92vh] w-full max-w-7xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#f8dde1] px-6 py-4">
          <div>
            <h3 className="text-[18px] font-black text-black">Driver Full Details</h3>
            <p className="mt-1 text-[12px] font-semibold text-slate-400">{driverName}{driverCode ? ` (${driverCode})` : ""}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-[#fff4f6] hover:text-[#df6374]">
            <X size={20} />
          </button>
        </div>

        <div className="max-h-[calc(92vh-74px)] overflow-y-auto bg-[#fffafa] p-6">
          {loading ? (
            <div className="flex min-h-[280px] items-center justify-center text-sm font-bold text-slate-400">Loading driver full details...</div>
          ) : error ? (
            <div className="rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">{error}</div>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-3 md:grid-cols-4">
                <CountBox label="Total orders" value={summary.totalOrders ?? summary.total_orders} />
                <CountBox label="Delivered orders" value={summary.deliveredOrders ?? summary.delivered_orders} />
                <MoneyBox label="Total income" value={summary.totalIncome ?? summary.total_income ?? summary.deliveryEarning ?? summary.delivery_earning} helper="Actual credited delivery earning" />
                <MoneyBox label="Wallet balance" value={wallet.availableBalance ?? wallet.available_balance} helper="Available balance" />
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                  <h4 className="mb-3 text-[15px] font-black text-black">Profile</h4>
                  <div className="grid gap-3 md:grid-cols-2">
                    <InfoBox label="Name" value={driverName} />
                    <InfoBox label="Phone" value={driverPhone} />
                    <InfoBox label="Email" value={driverEmail} />
                    <InfoBox label="Vehicle" value={vehicle} />
                    <InfoBox label="City" value={driver.city || "-"} />
                    <InfoBox label="Hub" value={driver.hub || "-"} />
                    <InfoBox label="Status" value={titleCase(driver.status)} />
                    <InfoBox label="Approval" value={titleCase(driver.approvalStatus || driver.approval_status)} />
                    <InfoBox label="Online" value={presence.isOnline || presence.is_online ? "Yes" : "No"} />
                    <InfoBox label="Available" value={presence.isAvailable || presence.is_available ? "Yes" : "No"} />
                    <InfoBox label="Last seen" value={dt(presence.lastSeenAt || presence.last_seen_at)} />
                    <InfoBox label="Joined" value={dt(driver.joinedAt || driver.joined_at)} />
                  </div>
                </div>

                <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                  <h4 className="mb-3 text-[15px] font-black text-black">Wallet & Withdrawals</h4>
                  <div className="grid gap-3 md:grid-cols-2">
                    <InfoBox label="Available balance" value={`₹ ${money(wallet.availableBalance ?? wallet.available_balance)}`} />
                    <InfoBox label="Locked balance" value={`₹ ${money(wallet.lockedBalance ?? wallet.locked_balance)}`} />
                    <InfoBox label="Lifetime earning" value={`₹ ${money(wallet.lifetimeEarnings ?? wallet.lifetime_earnings)}`} />
                    <InfoBox label="Lifetime withdrawn" value={`₹ ${money(wallet.lifetimeWithdrawn ?? wallet.lifetime_withdrawn)}`} />
                    <InfoBox label="Total credit" value={`₹ ${money(summary.totalCredit ?? summary.total_credit)}`} />
                    <InfoBox label="Total debit" value={`₹ ${money(summary.totalDebit ?? summary.total_debit)}`} />
                    <InfoBox label="Withdraw requested" value={`₹ ${money(summary.withdrawalRequestedAmount ?? summary.withdrawal_requested_amount)}`} />
                    <InfoBox label="Withdraw paid" value={`₹ ${money(summary.withdrawalPaidAmount ?? summary.withdrawal_paid_amount)}`} />
                    <InfoBox label="Withdraw pending" value={`₹ ${money(summary.withdrawalPendingAmount ?? summary.withdrawal_pending_amount)}`} />
                    <InfoBox label="Withdraw rejected" value={`₹ ${money(summary.withdrawalRejectedAmount ?? summary.withdrawal_rejected_amount)}`} />
                    <InfoBox label="Primary bank" value={primaryBank ? `${firstValue(primaryBank.bankName, primaryBank.bank_name)} / ${firstValue(primaryBank.accountNumberMasked, primaryBank.account_number_masked, "-")}` : "-"} />
                    <InfoBox label="UPI" value={primaryBank ? firstValue(primaryBank.upiId, primaryBank.upi_id, "-") : "-"} />
                  </div>
                </div>
              </div>

              <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h4 className="text-[15px] font-black text-black">Order Income History</h4>
                  <p className="text-[11px] font-bold text-slate-400">{ordersTotal} total orders</p>
                </div>
                <OrdersTable rows={orders} />
                {ordersTotal > ordersLimit ? (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-[#f8dde1] bg-[#fffafa] px-4 py-3">
                    <p className="text-[12px] font-bold text-slate-500">
                      Page {ordersPage} of {totalOrderPages} · Showing {orders.length} of {ordersTotal}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={ordersPage <= 1 || loading}
                        onClick={() => onOrdersPageChange?.(Math.max(1, Number(ordersPage) - 1))}
                        className="rounded-[10px] border border-[#f3c7ce] px-3 py-2 text-[12px] font-black text-[#df6374] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        disabled={ordersPage >= totalOrderPages || loading}
                        onClick={() => onOrdersPageChange?.(Math.min(totalOrderPages, Number(ordersPage) + 1))}
                        className="rounded-[10px] border border-[#f3c7ce] px-3 py-2 text-[12px] font-black text-[#df6374] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="grid gap-5 xl:grid-cols-2">
                <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h4 className="text-[15px] font-black text-black">Wallet Transactions</h4>
                    <p className="text-[11px] font-bold text-slate-400">Latest 25</p>
                  </div>
                  <TransactionsTable rows={transactions} />
                </div>

                <div className="rounded-[16px] border border-[#f3c7ce] bg-white p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h4 className="text-[15px] font-black text-black">Withdrawal History</h4>
                    <p className="text-[11px] font-bold text-slate-400">Latest 20</p>
                  </div>
                  <WithdrawalsTable rows={withdrawals} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
